'use client';

import useLocalStorage from '@/hooks/useLocalStorage';
import { Page } from '@/hooks/usePage';
import { Subject } from '@/hooks/useSubject';
import { slugify } from '@/utils/strings';
import { mdiArrowRightCircle, mdiMenuOpen } from '@mdi/js';
import { useCallback, useLayoutEffect, useState } from 'react';
import { EditorBlock } from '@/components/Redactie/Page/PagesEditor';
import Timeline from './Timeline';
import { useReducedMotion } from 'framer-motion';
import { useRouter } from 'next/navigation';
import TextButton from '@/components/Button/TextButton';
import IconButton from '../Button/IconButton';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';

const AccessibleTimeline = ({
    subject,
    page,
    nextPageId,
    nextChapter,
    blocks,
}: {
    subject: Subject;
    page: Page;
    nextPageId?: number | null;
    nextChapter?: {
        id: number;
        title: string;
    } | null;
    blocks: Array<
        {
            id?: number;
            type: string;
            data: Record<string, never>;
            order: number;
            editor_only: boolean;
        } & EditorBlock
    >;
}) => {
    const reduceMotion = useReducedMotion();
    const router = useRouter();

    const { subjectProgress, setPageProgress } = useSubjectProgress(subject.id);

    // Is rendered
    const [canShowTimeline, setCanShowTimeline] = useState(false);
    useLayoutEffect(() => {
        setCanShowTimeline(true);
    }, []);

    // Is finished
    const [isFinished, setIsFinished] = useState(false);
    useLayoutEffect(() => {
        setIsFinished(false);
    }, [page.id]);

    // Screenreader announcements
    const [isScreenReaderAnnouncementsEnabledLocalStorage] = useLocalStorage(
        'isScreenReaderAnnouncementsEnabled',
        true,
    );
    const [
        isScreenReaderAnnouncementsEnabled,
        setIsScreenReaderAnnouncementsEnabled,
    ] = useState(true);

    // Animated chat
    const [isAnimated, setIsAnimated] = useState(true);
    const [isStaticChatTimelineEnabled] = useLocalStorage(
        'isStaticChatTimelineEnabled',
        false,
    );

    // Focus mode
    const [isFocusModeEnabled, setIsFocusModeEnabled] = useLocalStorage(
        'isFocusModeEnabled',
        false,
    );

    // Scroll down when content height is changed
    const onContentHeightChanged = useCallback(() => {
        const scroller = document.querySelector('#accessibletimelinescroller');
        if (scroller && isAnimated) {
            if (reduceMotion) {
                scroller.scrollTop = scroller.scrollHeight;
            } else {
                scroller.scrollTo({
                    top: scroller.scrollHeight,
                    behavior: 'smooth',
                });
            }
        }
    }, [isAnimated, reduceMotion]);

    // Use layout effect to prevent 'html doesn't match server' error
    useLayoutEffect(() => {
        const hasFinishedPage = subjectProgress.pages?.[page.id];
        setIsAnimated(!isStaticChatTimelineEnabled && !hasFinishedPage);
        setIsScreenReaderAnnouncementsEnabled(
            isScreenReaderAnnouncementsEnabledLocalStorage,
        );
    }, [
        isStaticChatTimelineEnabled,
        isScreenReaderAnnouncementsEnabledLocalStorage,
    ]);

    // Next page url
    const nextPageUrl =
        !!nextChapter && !!nextPageId
            ? `/story/${subject.id}/${slugify(subject.title)}/${
                  nextChapter.id
              }/${slugify(nextChapter.title)}/${nextPageId}`
            : null;

    // Prefetch next page
    useLayoutEffect(() => {
        if (nextPageUrl) {
            router.prefetch(nextPageUrl);
        }
    }, [nextPageUrl]);

    return (
        <>
            {/* Show button to disable focus mode if in focus mode */}
            {canShowTimeline && isFocusModeEnabled && (
                <>
                    <div className="fixed top-4 md:top-28 z-200 p-2 bg-primary-700 rounded-full shadow-lg lg:-translate-x-11">
                        <IconButton
                            icon={mdiMenuOpen}
                            title="Zijbalk tonen"
                            onClick={() => {
                                setIsFocusModeEnabled(false);
                            }}
                            isExtraContrast
                        />
                    </div>
                    <div className="h-16" />
                </>
            )}

            {/* Start timeline if intro is finished */}
            {canShowTimeline && subjectProgress.hasSeenIntro && (
                <Timeline
                    subjectId={subject.id}
                    chapterId={page.chapter_id}
                    pageId={page.id}
                    blocks={blocks}
                    isAnimated={isAnimated}
                    isScreenReaderAnnouncementsEnabled={
                        isScreenReaderAnnouncementsEnabled
                    }
                    onContentHeightChanged={onContentHeightChanged}
                    onFinished={() => {
                        setIsFinished(true);
                        setPageProgress(page.id, true);
                        onContentHeightChanged();
                    }}
                />
            )}

            {/* Show button to continue story if timeline is finished and there is a next page */}
            {isFinished && !!nextPageUrl && (
                <TextButton
                    icon={mdiArrowRightCircle}
                    className="mt-8"
                    onClick={() => {
                        router.push(nextPageUrl);
                    }}
                >
                    Volgende
                </TextButton>
            )}
        </>
    );
};

export default AccessibleTimeline;
